import { useEffect, useState, useCallback, useMemo } from 'react'
import { Link, useParams } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { colors } from '../styles/tokens'
import { Button, Input, Select } from '../components/FormControls'
import { EmptyState, ErrorBanner, SuccessBanner, SkeletonRows } from '../components/Feedback'
import { parseResourceFile, validateResourceRows, downloadResourceTemplate } from '../lib/costCodeResourceImport'

// =======================================================================
// Resource Dictionary — manages estimation.resource_library, the single
// existing source of truth for resource IDENTITY (code/description/
// unit/type). Already consumed directly by rate_buildup_line,
// resource_rate, resource_costing_profile and resource_quantity_output
// via resource_library_id.
//
// This page manages IDENTITY ONLY. Resource Rate (effective-dated
// pricing history) is a completely separate concern and is untouched
// here — no rates, effective dates, pricing_date, currency, or rate
// selection logic. See FirstPrinciplesLibraryPage.jsx for Resource Rate
// management.
//
// resource_type uses the four values already established elsewhere in
// the codebase (see FirstPrinciplesLibraryPage.jsx RESOURCE_TYPES) and
// confirmed present in the actual data.
//
// Write access (create/edit) is enforced server-side by RLS
// (is_platform_admin()), from the existing Batch A migration — this
// page does not reproduce that check client-side. Every authenticated
// user can view; only a platform admin's INSERT/UPDATE will actually
// succeed, and any rejection is surfaced verbatim (same pattern as
// CostCodeDictionaryPage.jsx / FirstPrinciplesLibraryPage.jsx).
// =======================================================================

const RESOURCE_TYPES = [
  { value: 'material', label: 'Material' },
  { value: 'labour', label: 'Labour' },
  { value: 'equipment', label: 'Equipment' },
  { value: 'subcontract', label: 'Subcontract' },
]

export default function ResourceDictionaryPage() {
  // Tender-scoped (approved architecture): this Dictionary now belongs
  // to exactly one Tender, matched by route -- /tender/:tenderId/resources.
  // There is no longer a "company-wide" view; tenderId is required for
  // every read/write below.
  const { tenderId } = useParams()
  const [resources, setResources] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [successMsg, setSuccessMsg] = useState(null)
  const [search, setSearch] = useState('')
  const [typeFilter, setTypeFilter] = useState('all')
  const [showInactive, setShowInactive] = useState(false)

  // Create form state
  const [newCode, setNewCode] = useState('')
  const [newDescription, setNewDescription] = useState('')
  const [newUnit, setNewUnit] = useState('')
  const [newType, setNewType] = useState('material')
  const [creating, setCreating] = useState(false)

  // Edit-in-place state
  const [editingId, setEditingId] = useState(null)
  const [editCode, setEditCode] = useState('')
  const [editDescription, setEditDescription] = useState('')
  const [editUnit, setEditUnit] = useState('')
  const [editType, setEditType] = useState('material')
  const [savingId, setSavingId] = useState(null)

  // Import
  const [showImport, setShowImport] = useState(false)
  const [importRows, setImportRows] = useState(null)
  const [importParseError, setImportParseError] = useState(null)
  const [importing, setImporting] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    // Explicit tender_id filter -- never relies on RLS alone for
    // page-level scoping (RLS is the security backstop; this is the
    // correct dataset the page should show, independent of whether the
    // user happens to be authorized for other tenders too).
    const { data, error } = await supabase
      .from('resource_library')
      .select('id, code, description, unit, resource_type, is_active')
      .eq('tender_id', tenderId)
      .order('code')
    if (error) setError(error.message)
    else setResources(data || [])
    setLoading(false)
  }, [tenderId])

  // Switching Tender (route param change) must reload and never show
  // stale data from the previous Tender -- clearing resources
  // immediately on tenderId change, before the new load() resolves,
  // closes that window.
  useEffect(() => { setResources([]); load() }, [tenderId, load])

  async function handleCreate(e) {
    e.preventDefault()
    setError(null); setSuccessMsg(null)
    if (!newCode.trim() || !newDescription.trim()) {
      setError('Code and Description are required.')
      return
    }
    setCreating(true)
    // Tender-scoped RPC -- not a direct INSERT. create_resource()
    // derives/validates the caller's authorization for this specific
    // tender_id server-side; the frontend never writes tender_id (or
    // organization_id) as a trusted value on its own.
    const { error } = await supabase.rpc('create_resource', {
      p_tender_id: tenderId,
      p_code: newCode.trim(),
      p_resource_type: newType,
      p_description: newDescription.trim(),
      p_unit: newUnit.trim() || null,
    })
    if (error) {
      setError(`Could not create Resource — ${error.message}`)
    } else {
      setSuccessMsg('Resource created.')
      setNewCode(''); setNewDescription(''); setNewUnit(''); setNewType('material')
      await load()
    }
    setCreating(false)
  }

  function startEdit(r) {
    setEditingId(r.id)
    setEditCode(r.code || '')
    setEditDescription(r.description || '')
    setEditUnit(r.unit || '')
    setEditType(r.resource_type || 'material')
    setError(null); setSuccessMsg(null)
  }

  function cancelEdit() {
    setEditingId(null)
  }

  async function handleSaveEdit(id) {
    setError(null); setSuccessMsg(null)
    if (!editCode.trim() || !editDescription.trim()) {
      setError('Code and Description are required.')
      return
    }
    setSavingId(id)
    const { error } = await supabase.from('resource_library').update({
      code: editCode.trim(),
      description: editDescription.trim(),
      unit: editUnit.trim() || null,
      resource_type: editType,
    }).eq('id', id)
    if (error) {
      setError(`Could not update Resource — ${error.message}`)
    } else {
      setSuccessMsg('Resource updated.')
      setEditingId(null)
      await load()
    }
    setSavingId(null)
  }

  async function handleToggleActive(r) {
    setError(null); setSuccessMsg(null)
    setSavingId(r.id)
    const { error } = await supabase.from('resource_library')
      .update({ is_active: !r.is_active }).eq('id', r.id)
    if (error) {
      setError(`Could not ${r.is_active ? 'deactivate' : 'reactivate'} Resource — ${error.message}`)
    } else {
      setSuccessMsg(r.is_active ? 'Resource deactivated.' : 'Resource reactivated.')
      await load()
    }
    setSavingId(null)
  }

  async function handleImportFile(e) {
    const file = e.target.files?.[0]
    if (!file) return
    setImportParseError(null)
    setImportRows(null)
    try {
      const rows = await parseResourceFile(file)
      setImportRows(validateResourceRows(rows))
    } catch (err) {
      setImportParseError(err.message)
    }
    e.target.value = ''
  }

  async function handleConfirmImport() {
    if (!importRows) return
    setImporting(true)
    setError(null); setSuccessMsg(null)
    const payload = importRows.map((r) => ({
      code: r.code, resource_type: r.resource_type, description: r.description, unit: r.unit || null,
    }))
    const { data, error } = await supabase.rpc('import_resources', { p_tender_id: tenderId, p_rows: payload })
    if (error) {
      setError(`Import failed — ${error.message}`)
    } else {
      setSuccessMsg(`Imported ${data.inserted_count} Resource(s).`)
      setShowImport(false)
      setImportRows(null)
      await load()
    }
    setImporting(false)
  }

  const typeLabel = useMemo(() => Object.fromEntries(RESOURCE_TYPES.map((t) => [t.value, t.label])), [])

  const visibleResources = useMemo(() => {
    const term = search.trim().toLowerCase()
    return resources
      .filter((r) => showInactive || r.is_active)
      .filter((r) => typeFilter === 'all' || r.resource_type === typeFilter)
      .filter((r) => !term || r.code.toLowerCase().includes(term) || (r.description || '').toLowerCase().includes(term))
      .sort((a, b) => (a.code || '').localeCompare(b.code || ''))
  }, [resources, search, typeFilter, showInactive])

  return (
    <div>
      <Link to={`/estimation/tender/${tenderId}`} style={{ fontSize: 13, color: colors.textSecondary, textDecoration: 'none' }}>
        &larr; Back to Tender
      </Link>
      <div style={{ marginBottom: 16, marginTop: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
        <div>
          <div style={{ fontSize: 20, fontWeight: 700, color: colors.ink }}>Resource Dictionary</div>
          <div style={{ fontSize: 13, color: colors.textSecondary }}>
            This Tender's Resource master (identity only) used across Rate Build-Up and First Principles pricing. Rates and pricing history are managed separately.
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button variant="secondary" onClick={downloadResourceTemplate}>Download Template</Button>
          <Button variant="secondary" onClick={() => setShowImport((s) => !s)}>
            {showImport ? 'Cancel Import' : 'Import from Excel'}
          </Button>
        </div>
      </div>

      {error && <ErrorBanner message={error} />}
      {successMsg && <SuccessBanner message={successMsg} />}

      {showImport && (
        <div style={{ padding: 16, border: `1px solid ${colors.border}`, borderRadius: 8, marginBottom: 20, background: colors.surfaceAlt }}>
          <input type="file" accept=".xlsx" onChange={handleImportFile} style={{ marginBottom: 12 }} />
          {importParseError && <ErrorBanner message={importParseError} />}
          {importRows && (
            <>
              <div style={{ fontSize: 13, marginBottom: 8 }}>
                Total: {importRows.length} | Valid: {importRows.filter((r) => r.status === 'valid').length} | Errors: {importRows.filter((r) => r.status === 'error').length}
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, marginBottom: 12 }}>
                <thead>
                  <tr style={{ borderBottom: `1px solid ${colors.border2}`, textAlign: 'left' }}>
                    <th style={{ padding: 4 }}>Row</th><th style={{ padding: 4 }}>Code</th><th style={{ padding: 4 }}>Status</th><th style={{ padding: 4 }}>Issues</th>
                  </tr>
                </thead>
                <tbody>
                  {importRows.map((r) => (
                    <tr key={r.rowNumber} style={{ borderBottom: `1px solid ${colors.border}`, color: r.status === 'error' ? '#B91C1C' : colors.ink }}>
                      <td style={{ padding: 4 }}>{r.rowNumber}</td>
                      <td style={{ padding: 4 }}>{r.code || '—'}</td>
                      <td style={{ padding: 4 }}>{r.status}</td>
                      <td style={{ padding: 4 }}>{r.issues.join(' ')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <Button onClick={handleConfirmImport}
                disabled={importing || importRows.length === 0 || importRows.some((r) => r.status === 'error')}>
                {importing ? 'Importing…' : `Confirm Import (${importRows.filter((r) => r.status === 'valid').length})`}
              </Button>
            </>
          )}
        </div>
      )}

      {/* Create form */}
      <form onSubmit={handleCreate} style={{
        display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'flex-end',
        padding: 16, border: `1px solid ${colors.border}`, borderRadius: 8, marginBottom: 20, background: colors.surfaceAlt,
      }}>
        <div>
          <label style={{ fontSize: 12, color: colors.textSecondary, display: 'block', marginBottom: 4 }}>Code *</label>
          <Input value={newCode} onChange={(e) => setNewCode(e.target.value)} placeholder="e.g. CEM-001" style={{ width: 140 }} />
        </div>
        <div>
          <label style={{ fontSize: 12, color: colors.textSecondary, display: 'block', marginBottom: 4 }}>Description *</label>
          <Input value={newDescription} onChange={(e) => setNewDescription(e.target.value)} placeholder="e.g. Portland Cement" style={{ width: 240 }} />
        </div>
        <div>
          <label style={{ fontSize: 12, color: colors.textSecondary, display: 'block', marginBottom: 4 }}>Unit</label>
          <Input value={newUnit} onChange={(e) => setNewUnit(e.target.value)} placeholder="e.g. ton" style={{ width: 100 }} />
        </div>
        <div>
          <label style={{ fontSize: 12, color: colors.textSecondary, display: 'block', marginBottom: 4 }}>Type *</label>
          <Select value={newType} onChange={(e) => setNewType(e.target.value)} style={{ width: 160 }}>
            {RESOURCE_TYPES.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </Select>
        </div>
        <Button type="submit" disabled={creating}>{creating ? 'Creating…' : '+ Add Resource'}</Button>
      </form>

      {/* Search / filter */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 12 }}>
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search code or description…" style={{ width: 260 }} />
        <Select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} style={{ width: 160 }}>
          <option value="all">All types</option>
          {RESOURCE_TYPES.map((t) => (
            <option key={t.value} value={t.value}>{t.label}</option>
          ))}
        </Select>
        <label style={{ fontSize: 13, color: colors.textSecondary, display: 'flex', alignItems: 'center', gap: 6 }}>
          <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
          Show deactivated
        </label>
        <div style={{ fontSize: 12, color: colors.textSecondary, marginLeft: 'auto' }}>{visibleResources.length} resource(s)</div>
      </div>

      {loading ? (
        <SkeletonRows rows={6} cols={6} />
      ) : visibleResources.length === 0 ? (
        <EmptyState title="No Resources found" description="Create the first Resource using the form above, or adjust your search." />
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: `2px solid ${colors.border2}`, textAlign: 'left' }}>
              <th style={{ padding: '8px 6px' }}>Code</th>
              <th style={{ padding: '8px 6px' }}>Description</th>
              <th style={{ padding: '8px 6px' }}>Unit</th>
              <th style={{ padding: '8px 6px' }}>Type</th>
              <th style={{ padding: '8px 6px' }}>Status</th>
              <th style={{ padding: '8px 6px', textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {visibleResources.map((r) => {
              const isEditing = editingId === r.id
              return (
                <tr key={r.id} style={{ borderBottom: `1px solid ${colors.border}`, opacity: r.is_active ? 1 : 0.55 }}>
                  {isEditing ? (
                    <>
                      <td style={{ padding: '6px' }}><Input value={editCode} onChange={(e) => setEditCode(e.target.value)} style={{ width: 110 }} /></td>
                      <td style={{ padding: '6px' }}><Input value={editDescription} onChange={(e) => setEditDescription(e.target.value)} style={{ width: 220 }} /></td>
                      <td style={{ padding: '6px' }}><Input value={editUnit} onChange={(e) => setEditUnit(e.target.value)} style={{ width: 80 }} /></td>
                      <td style={{ padding: '6px' }}>
                        <Select value={editType} onChange={(e) => setEditType(e.target.value)} style={{ width: 140 }}>
                          {RESOURCE_TYPES.map((t) => (
                            <option key={t.value} value={t.value}>{t.label}</option>
                          ))}
                        </Select>
                      </td>
                      <td style={{ padding: '6px' }}>{r.is_active ? 'Active' : 'Inactive'}</td>
                      <td style={{ padding: '6px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                        <Button size="sm" variant="secondary" onClick={cancelEdit} disabled={savingId === r.id}>Cancel</Button>{' '}
                        <Button size="sm" onClick={() => handleSaveEdit(r.id)} disabled={savingId === r.id}>
                          {savingId === r.id ? 'Saving…' : 'Save'}
                        </Button>
                      </td>
                    </>
                  ) : (
                    <>
                      <td style={{ padding: '8px 6px', fontFamily: 'monospace' }}>{r.code}</td>
                      <td style={{ padding: '8px 6px' }}>{r.description}</td>
                      <td style={{ padding: '8px 6px', color: colors.textSecondary }}>{r.unit || '—'}</td>
                      <td style={{ padding: '8px 6px', color: colors.textSecondary }}>{typeLabel[r.resource_type] || r.resource_type}</td>
                      <td style={{ padding: '8px 6px' }}>{r.is_active ? 'Active' : 'Inactive'}</td>
                      <td style={{ padding: '8px 6px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                        <Button size="sm" variant="secondary" onClick={() => startEdit(r)}>Edit</Button>{' '}
                        <Button size="sm" variant="secondary" onClick={() => handleToggleActive(r)} disabled={savingId === r.id}>
                          {savingId === r.id ? '…' : r.is_active ? 'Deactivate' : 'Reactivate'}
                        </Button>
                      </td>
                    </>
                  )}
                </tr>
              )
            })}
          </tbody>
        </table>
      )}
    </div>
  )
}
